import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  NotificationsActive as NotificationsIcon,
  LocalShipping as ShippingIcon,
  ShoppingCart as CartIcon,
  LocalOffer as OfferIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import usePushNotifications from '../../hooks/usePushNotifications';

/**
 * NotificationPermissionDialog
 *
 * Beautiful Material-UI dialog that explains benefits and requests permission
 * for push notifications
 */
const NotificationPermissionDialog = ({ open, onClose, user }) => {
  const { requestPermission, unregisterDevice, permission, deviceToken, isLoading, error } = usePushNotifications(user);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [permissionDisabled, setPermissionDisabled] = useState(false);

  // Browser permission alone does not confirm successful device enrollment.
  const isDisabled = localStorage.getItem('notifications_disabled') === 'true';
  const isEnabled = permission === 'granted' && Boolean(deviceToken) && !isDisabled;

  const handleEnableNotifications = async () => {
    const granted = await requestPermission();

    if (granted) {
      setPermissionGranted(true);
      // Close dialog after 2 seconds to show success message
      setTimeout(() => {
        onClose();
        setPermissionGranted(false);
      }, 2000);
    }
  };

  const handleDisableNotifications = async () => {
    await unregisterDevice();
    setPermissionDisabled(true);

    // Close dialog after 2 seconds
    setTimeout(() => {
      onClose();
      setPermissionDisabled(false);
    }, 2000);
  };

  const handleMaybeLater = () => {
    // Mark that user has seen the prompt
    localStorage.setItem('notification_prompt_dismissed_at', Date.now());
    onClose();
  };

  const benefits = [
    {
      icon: <ShippingIcon />,
      primary: 'Order Updates',
      secondary: 'Get real-time updates when your order ships and arrives'
    },
    {
      icon: <CartIcon />,
      primary: 'Cart Reminders',
      secondary: 'Never forget items in your cart - we\'ll remind you'
    },
    {
      icon: <OfferIcon />,
      primary: 'Exclusive Offers',
      secondary: 'Be first to know about sales and special promotions'
    },
    {
      icon: <CheckIcon />,
      primary: 'Back in Stock Alerts',
      secondary: 'Get notified when your favorite items are available again'
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleMaybeLater}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(to bottom, #fff 0%, #f9f9f9 100%)'
        }
      }}
    >
      {permissionGranted ? (
        // Success state - Enabled
        <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
          <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            You're All Set!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You'll now receive important updates and exclusive offers
          </Typography>
        </Box>
      ) : permissionDisabled ? (
        // Success state - Disabled
        <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
          <NotificationsIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Notifications Disabled
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You can re-enable notifications anytime from your profile settings
          </Typography>
        </Box>
      ) : isEnabled ? (
        // Already enabled - show manage options
        <>
          <DialogTitle sx={{ textAlign: 'center', pb: 1, pt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <CheckIcon
                sx={{
                  fontSize: 60,
                  color: 'success.main'
                }}
              />
            </Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              Notifications Enabled
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You're currently receiving push notifications
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 2, pb: 1 }}>
            <Box
              sx={{
                p: 3,
                backgroundColor: 'success.lighter',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'success.light',
                mb: 2
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                ✅ You're receiving notifications for:
              </Typography>
              <List dense sx={{ py: 0 }}>
                {benefits.map((benefit, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box sx={{ color: 'success.dark', display: 'flex' }}>
                        {benefit.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={benefit.primary}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box
              sx={{
                p: 2,
                backgroundColor: 'warning.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.light'
              }}
            >
              <Typography variant="caption" color="text.secondary">
                ⚠️ If you disable notifications, you won't receive important order updates,
                back-in-stock alerts, or exclusive offers.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
            <Button
              onClick={onClose}
              disabled={isLoading}
              sx={{
                minWidth: 120,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleDisableNotifications}
              variant="outlined"
              color="error"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <NotificationsIcon />}
              sx={{
                minWidth: 200,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {isLoading ? 'Disabling...' : 'Disable Notifications'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ textAlign: 'center', pb: 1, pt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <NotificationsIcon
                sx={{
                  fontSize: 60,
                  color: 'primary.main',
                  animation: 'bell-ring 1s ease-in-out infinite'
                }}
              />
            </Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              Stay in the Loop
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Enable notifications to never miss important updates
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 2, pb: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <List sx={{ py: 0 }}>
              {benefits.map((benefit, index) => (
                <ListItem
                  key={index}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 1
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {benefit.icon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {benefit.primary}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {benefit.secondary}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'info.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.light'
              }}
            >
              <Typography variant="caption" color="text.secondary">
                🔒 Your privacy matters. You can change your notification preferences
                anytime in your account settings. We'll only send you important updates
                and offers you choose to receive.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
            <Button
              onClick={handleMaybeLater}
              disabled={isLoading}
              sx={{
                minWidth: 120,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Maybe Later
            </Button>
            <Button
              onClick={handleEnableNotifications}
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <NotificationsIcon />}
              sx={{
                minWidth: 200,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              {isLoading ? 'Enabling...' : 'Enable Notifications'}
            </Button>
          </DialogActions>
        </>
      )}

      <style>
        {`
          @keyframes bell-ring {
            0%, 100% { transform: rotate(0deg); }
            10%, 30% { transform: rotate(-10deg); }
            20%, 40% { transform: rotate(10deg); }
            50% { transform: rotate(0deg); }
          }
        `}
      </style>
    </Dialog>
  );
};

export default NotificationPermissionDialog;
